import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp as LeadsIcon,
  SwapHoriz as ConvertIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
  writeBatch
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  LEAD_STATUS_ORDER,
  LEAD_STATUS_HEX,
  normalizeLeadStatus
} from '../utils/statusConstants';
import CommentsThread from './common/CommentsThread';
import LeadCard from './leads/LeadCard';
import LeadColumn from './leads/LeadColumn';
import { fallbackColorForUid } from '../utils/userColors';
import { openExternalUrl } from '../utils/urlHelpers';

const STATUS_DESCRIPTIONS = {
  [LEAD_STATUSES.NEW]: 'We just found out about this lead. No contact yet.',
  [LEAD_STATUSES.DISCOVERY]: "We're actively learning about the lead's needs, goals, and potential fit.",
  [LEAD_STATUSES.DEALMAKING]: 'Offer made or terms being negotiated. Close to formal engagement.',
  [LEAD_STATUSES.HOLD]: 'Paused by mutual agreement or circumstance. Revisit later.',
  [LEAD_STATUSES.FROZEN]: 'Inactive for the foreseeable future.'
};

// Board columns in pipeline order. `converted` is a hidden status set by
// the convert-to-client flow only; it never appears on the board or in selects.
const LEAD_COLUMNS = LEAD_STATUS_ORDER.map(status => ({
  key: status,
  label: LEAD_STATUS_LABELS[status],
  color: LEAD_STATUS_HEX[status],
  description: STATUS_DESCRIPTIONS[status]
}));

export default function Leads() {
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const [leads, setLeads] = useState([]);
  const [partnerEntities, setPartnerEntities] = useState([]);
  const [usersByUid, setUsersByUid] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);
  // Optimistic status overrides applied while a drag's Firestore write is in flight
  const [statusOverrides, setStatusOverrides] = useState({});
  const [activeLead, setActiveLead] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const [formData, setFormData] = useState({
    contactName: '',
    businessName: '',
    status: LEAD_STATUSES.NEW,
    relationshipManagerUid: '',
    phone: '',
    email: '',
    url: '',
    lastContactedAt: '',
    nextFollowupAt: '',
    notes: ''
  });

  // Open "Add Lead" dialog automatically when navigated with ?new=1
  const newDialogOpenedRef = useRef(false);
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      if (!newDialogOpenedRef.current && params.get('new') === '1') {
        newDialogOpenedRef.current = true;
        handleOpenDialog();
      }
    } catch {}
  }, [location.search]);

  // Fetch partner entities for relationship manager dropdown
  const fetchPartnerEntities = async () => {
    try {
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, where('entityType', '==', 'partner'), where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      const partnersData = [];
      snapshot.forEach((doc) => {
        partnersData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setPartnerEntities(partnersData);
    } catch (error) {
      console.error('Error fetching partner entities:', error);
    }
  };

  // Fetch app users once for comment avatars (keyed by the `uid` field —
  // users docs created from App Users don't use the auth uid as doc id)
  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const byUid = {};
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (!data.uid) return;
        byUid[data.uid] = {
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
          color: data.color || fallbackColorForUid(data.uid)
        };
      });
      setUsersByUid(byUid);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Firestore operations
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const leadsRef = collection(db, 'leads');
      const q = query(leadsRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const leadsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          leadsData.push({
            id: doc.id,
            ...data,
            // Legacy statuses are normalized here for display/filtering and
            // persisted on the next save of the lead.
            status: normalizeLeadStatus(data.status),
            rawStatus: data.status,
            createdAtMs: data.createdAt?.toMillis?.() ?? Date.now(),
            createdAt: data.createdAt?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
          });
        });
        setLeads(leadsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching leads:', error);
      setSnackbar({ open: true, message: 'Error loading leads', severity: 'error' });
      setLoading(false);
    }
  };

  const addLead = async (leadData) => {
    try {
      setSaving(true);
      const docRef = await addDoc(collection(db, 'leads'), {
        ...leadData,
        createdAt: serverTimestamp(),
        relationshipManagerUid: leadData.relationshipManagerUid || '',
      });
      
      setSnackbar({ open: true, message: 'Lead added successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding lead:', error);
      setSnackbar({ open: true, message: 'Error adding lead', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateLead = async (leadId, leadData) => {
    try {
      setSaving(true);
      const leadRef = doc(db, 'leads', leadId);
      await updateDoc(leadRef, {
        ...leadData,
        relationshipManagerUid: leadData.relationshipManagerUid || '',
      });
      
      setSnackbar({ open: true, message: 'Lead updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating lead:', error);
      setSnackbar({ open: true, message: 'Error updating lead', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (leadId) => {
    try {
      // Firestore doesn't cascade subcollection deletes — remove comments first
      try {
        const commentsSnapshot = await getDocs(collection(db, 'leads', leadId, 'comments'));
        if (!commentsSnapshot.empty) {
          const batch = writeBatch(db);
          commentsSnapshot.forEach((commentDoc) => batch.delete(commentDoc.ref));
          await batch.commit();
        }
      } catch (error) {
        console.error('Error deleting lead comments:', error);
      }
      const leadRef = doc(db, 'leads', leadId);
      await deleteDoc(leadRef);
      setSnackbar({ open: true, message: 'Lead deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting lead:', error);
      setSnackbar({ open: true, message: 'Error deleting lead', severity: 'error' });
      throw error;
    }
  };

  // Get partner entity name for display
  const getPartnerName = (entityId) => {
    const partner = partnerEntities.find(p => p.id === entityId);
    return partner ? partner.entityName : 'Unknown Partner';
  };

  useEffect(() => {
    fetchPartnerEntities();
    fetchUsers();
    const unsubscribe = fetchLeads();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const boardLeads = useMemo(
    () => leads.map((lead) =>
      statusOverrides[lead.id] ? { ...lead, status: statusOverrides[lead.id] } : lead
    ),
    [leads, statusOverrides]
  );

  const handleDragStart = ({ active }) => {
    setActiveLead(boardLeads.find((l) => l.id === active.id) || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveLead(null);
    if (!over) return;
    const lead = boardLeads.find((l) => l.id === active.id);
    if (!lead || lead.status === over.id) return;

    setStatusOverrides((prev) => ({ ...prev, [lead.id]: over.id }));
    try {
      await updateDoc(doc(db, 'leads', lead.id), { status: over.id });
    } catch (error) {
      console.error('Error moving lead:', error);
      setSnackbar({ open: true, message: 'Error moving lead', severity: 'error' });
    } finally {
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[lead.id];
        return next;
      });
    }
  };

  const handleOpenDialog = (lead = null, defaultStatus = LEAD_STATUSES.NEW) => {
    if (lead) {
      setEditingLead(lead);
      setFormData({
        contactName: lead.contactName || '',
        businessName: lead.businessName || '',
        status: lead.status, // already normalized at read time
        relationshipManagerUid: lead.relationshipManagerUid || '',
        phone: lead.phone || '',
        email: lead.email || '',
        url: lead.url || '',
        lastContactedAt: lead.lastContactedAt || '',
        nextFollowupAt: lead.nextFollowupAt || '',
        notes: lead.notes || ''
      });
    } else {
      setEditingLead(null);
      setFormData({
        contactName: '',
        businessName: '',
        status: defaultStatus,
        relationshipManagerUid: '',
        phone: '',
        email: '',
        url: '',
        lastContactedAt: '',
        nextFollowupAt: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLead(null);
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.contactName || !formData.businessName) {
        setSnackbar({ open: true, message: 'Contact Name and Business Name are required', severity: 'error' });
        return;
      }

      const leadData = {
        ...formData,
        relationshipManagerUid: formData.relationshipManagerUid || '',
      };

      if (editingLead) {
        await updateLead(editingLead.id, leadData);
      } else {
        await addLead(leadData);
      }
      
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await deleteLead(id);
        handleCloseDialog();
      } catch (error) {
        // Error handling is done in the deleteLead function
      }
    }
  };

  const handleConvertToClient = async (lead) => {
    try {
      setSaving(true);
      
      // Check for duplicate Business Name in clients
      const clientsRef = collection(db, 'clients');
      const duplicateQuery = query(clientsRef, where('clientName', '==', lead.businessName));
      const duplicateSnapshot = await getDocs(duplicateQuery);
      
      if (!duplicateSnapshot.empty) {
        setSnackbar({ 
          open: true, 
          message: `❌ Conversion Failed: A Client with this Business Name already exists.`, 
          severity: 'error' 
        });
        return;
      }

      // Create client record
      const clientData = {
        clientName: lead.businessName,
        industry: lead.industry || '',
        location: lead.location || '',
        currency: 'USD',
        relationshipManagerUid: lead.relationshipManagerUid || '',
        googleDriveURL: '',
        createdFromLeadId: lead.id,
        status: 'Active',
        createdAt: serverTimestamp(),
      };

      // Use batch operations for atomic transaction
      const batch = writeBatch(db);
      
      // Create client record
      const clientRef = doc(collection(db, 'clients'));
      batch.set(clientRef, clientData);
      
      // Create contact record
      const contactRef = doc(collection(db, 'contacts'));
      const contactData = {
        fullName: lead.contactName,
        email: lead.email || '',
        phone: lead.phone || '',
        associatedClient: clientRef.id,
        isPrimaryBillingContact: true,
        notes: `Contact created from lead conversion. Original lead notes: ${lead.notes || ''}`,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid || '',
      };
      batch.set(contactRef, contactData);

      // Update lead status to converted
      const leadRef = doc(db, 'leads', lead.id);
      batch.update(leadRef, { 
        status: LEAD_STATUSES.CONVERTED,
        convertedAt: serverTimestamp(),
        convertedToClientId: clientRef.id
      });

      await batch.commit();
      
      setSnackbar({ 
        open: true, 
        message: `${lead.businessName} converted to client successfully!`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error converting lead to client:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error converting lead to client. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const openConvertDialog = (lead) => {
    setLeadToConvert(lead);
    setConvertDialogOpen(true);
  };

  const closeConvertDialog = () => {
    setConvertDialogOpen(false);
    setLeadToConvert(null);
  };

  const confirmConvert = async () => {
    if (!leadToConvert) return;
    await handleConvertToClient(leadToConvert);
    setConvertDialogOpen(false);
    setLeadToConvert(null);
  };

  const getRelationshipManager = (entityId) => {
    return getPartnerName(entityId);
  };

  // Current user identity for the comments thread (name/color from the
  // users collection, resolved by the auth uid field)
  const commentUser = {
    uid: currentUser?.uid,
    name:
      usersByUid[currentUser?.uid]?.name ||
      `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() ||
      currentUser?.email ||
      'Unknown user',
    color: usersByUid[currentUser?.uid]?.color || fallbackColorForUid(currentUser?.uid)
  };


  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LeadsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Leads Management
            </Typography>

          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Lead
        </Button>
      </Box>

      {/* Board */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${LEAD_COLUMNS.length}, minmax(240px, 1fr))`,
              gap: 2,
              flex: 1,
              minHeight: 0,
              overflowX: 'auto',
              pb: 1
            }}
          >
            {LEAD_COLUMNS.map((column) => (
              <LeadColumn
                key={column.key}
                column={column}
                leads={boardLeads.filter((lead) => lead.status === column.key)}
                getManagerName={(id) => (id ? getRelationshipManager(id) : null)}
                onOpenLead={handleOpenDialog}
              />
            ))}
          </Box>
          <DragOverlay dropAnimation={null}>
            {activeLead ? (
              <LeadCard
                lead={activeLead}
                managerName={activeLead.relationshipManagerUid ? getRelationshipManager(activeLead.relationshipManagerUid) : null}
                onClick={() => {}}
                overlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={editingLead ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle>
          {editingLead ? 'Edit Lead' : 'Add Lead'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3.5, minHeight: editingLead ? 480 : 'auto' }}>
            <Box sx={{ flex: editingLead ? 7 : 1, minWidth: 0 }}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    required
                    helperText="Required field"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    required
                    helperText="Required field - must be unique for conversion"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      {LEAD_COLUMNS.map(column => (
                        <MenuItem key={column.key} value={column.key}>{column.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Relationship Manager</InputLabel>
                    <Select
                      value={formData.relationshipManagerUid}
                      label="Relationship Manager"
                      onChange={(e) => setFormData({ ...formData, relationshipManagerUid: e.target.value })}
                    >
                      <MenuItem value="">Select Partner</MenuItem>
                      {partnerEntities.map(partner => (
                        <MenuItem key={partner.id} value={partner.id}>{partner.entityName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    helperText="Optional"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    helperText="Optional"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="URL"
                    placeholder="https://..."
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    helperText="Optional - paste a website, LinkedIn, or any relevant link"
                    InputProps={{
                      endAdornment: formData.url.trim() ? (
                        <InputAdornment position="end">
                          <Tooltip title="Open in new tab">
                            <IconButton
                              size="small"
                              onClick={() => openExternalUrl(formData.url)}
                              edge="end"
                            >
                              <OpenInNewIcon color="primary" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Contacted"
                    type="date"
                    value={formData.lastContactedAt}
                    onChange={(e) => setFormData({ ...formData, lastContactedAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Optional - when you last spoke with this lead"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Next Follow-up"
                    type="date"
                    value={formData.nextFollowupAt}
                    onChange={(e) => setFormData({ ...formData, nextFollowupAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Optional - when you plan to reach out next"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="General notes, observations, meeting logs, etc."
                    helperText="Optional - Add any relevant information about the lead"
                  />
                </Grid>
              </Grid>
            </Box>
            {editingLead && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: 5, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <CommentsThread
                    pathSegments={['leads', editingLead.id, 'comments']}
                    currentUser={commentUser}
                    usersByUid={usersByUid}
                    variant="lead"
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {editingLead && (
            <>
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(editingLead.id)}>
                Delete
              </Button>
              <Button
                color="success"
                startIcon={<ConvertIcon />}
                onClick={() => {
                  const lead = editingLead;
                  handleCloseDialog();
                  openConvertDialog(lead);
                }}
              >
                Convert to Client
              </Button>
              <Box sx={{ flex: 1 }} />
            </>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (editingLead ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Confirmation Dialog */}
      <Dialog open={convertDialogOpen} onClose={closeConvertDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Convert Lead to Client</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Convert "{leadToConvert?.businessName}" to a client? This will create a new client and a primary billing contact.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box><strong>Contact:</strong> {leadToConvert?.contactName || '-'}</Box>
            <Box><strong>Business:</strong> {leadToConvert?.businessName || '-'}</Box>
            <Box><strong>Relationship Manager:</strong> {getRelationshipManager(leadToConvert?.relationshipManagerUid) || '-'}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConvertDialog} disabled={saving}>Cancel</Button>
          <Button onClick={confirmConvert} variant="contained" color="success" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

  

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
} 